export type AmaShareResult = 'shared' | 'downloaded' | 'cancelled';

export interface AmaArtworkOptions {
  question: string;
  accentColor?: string;
  gradientStart?: string;
  gradientEnd?: string;
  gradientDirection?: string;
}

const BADGE_WIDTH = 640;
const BADGE_GUTTER = 32;
const BADGE_QUESTION_WIDTH = 500;
const STORY_WIDTH = 1080;
const STORY_HEIGHT = 1920;
const MAX_QUESTION_GRAPHEMES = 280;
const CTA_TEXT = 'Ask Me Anything - Click ShoutOut link in my bio';
const ICON_URL = '/whiteicon.png';

let shoutOutIcon: HTMLImageElement | null = null;

function safeHexColor(color: string | undefined, fallback: string): string {
  return color && /^#[0-9a-f]{6}$/i.test(color) ? color : fallback;
}

function isLightColor(color: string): boolean {
  const red = Number.parseInt(color.slice(1, 3), 16);
  const green = Number.parseInt(color.slice(3, 5), 16);
  const blue = Number.parseInt(color.slice(5, 7), 16);
  return ((red * 299) + (green * 587) + (blue * 114)) / 1000 > 165;
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.min(radius, width / 2, height / 2);
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + width, y, x + width, y + height, r);
  context.arcTo(x + width, y + height, x, y + height, r);
  context.arcTo(x, y + height, x, y, r);
  context.arcTo(x, y, x + width, y, r);
  context.closePath();
}

function splitGraphemes(value: string): string[] {
  type Segment = { segment: string };
  type Segmenter = { segment: (input: string) => Iterable<Segment> };
  type SegmenterConstructor = new (
    locales?: string | string[],
    options?: { granularity: 'grapheme' },
  ) => Segmenter;

  const SegmenterClass = (Intl as typeof Intl & { Segmenter?: SegmenterConstructor }).Segmenter;
  if (SegmenterClass) {
    const segmenter = new SegmenterClass(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(value), ({ segment }) => segment);
  }

  return Array.from(value);
}

function normalizeQuestion(question: string): string {
  const normalized = question.trim().replace(/\s+/g, ' ');
  return splitGraphemes(normalized).slice(0, MAX_QUESTION_GRAPHEMES).join('') || 'Ask me anything';
}

function wrapText(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  for (const word of words) {
    // Split unusually long URL-like strings without breaking emoji sequences.
    if (context.measureText(word).width > maxWidth) {
      if (current) {
        lines.push(current);
        current = '';
      }

      let chunk = '';
      for (const grapheme of splitGraphemes(word)) {
        const candidate = `${chunk}${grapheme}`;
        if (chunk && context.measureText(candidate).width > maxWidth) {
          lines.push(chunk);
          chunk = grapheme;
        } else {
          chunk = candidate;
        }
      }
      current = chunk;
      continue;
    }

    const candidate = current ? `${current} ${word}` : word;
    if (current && context.measureText(candidate).width > maxWidth) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  }

  if (current) lines.push(current);
  return lines.length > 0 ? lines : [''];
}

function fitQuestionText(
  context: CanvasRenderingContext2D,
  question: string,
): { fontSize: number; lineHeight: number; lines: string[] } {
  for (let fontSize = 44; fontSize >= 24; fontSize -= 2) {
    context.font = `700 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
    const lines = wrapText(context, question, BADGE_QUESTION_WIDTH);
    const lineHeight = Math.round(fontSize * 1.3);
    if (lines.length * lineHeight <= 7 * 31) {
      return { fontSize, lineHeight, lines };
    }
  }

  const fontSize = 24;
  context.font = `700 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  return {
    fontSize,
    lineHeight: Math.round(fontSize * 1.3),
    lines: wrapText(context, question, BADGE_QUESTION_WIDTH),
  };
}

function dataUrlToFile(dataUrl: string, filename: string): File {
  const commaIndex = dataUrl.indexOf(',');
  const binary = atob(dataUrl.slice(commaIndex + 1));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new File([bytes], filename, { type: 'image/png' });
}

function drawFallbackShoutOutIcon(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
) {
  // A compact megaphone fallback keeps the badge branded if the cached image
  // has not decoded yet when the creator taps Share.
  context.save();
  context.translate(x, y);
  context.fillStyle = '#ffffff';
  context.beginPath();
  context.moveTo(size * 0.18, size * 0.42);
  context.lineTo(size * 0.44, size * 0.42);
  context.lineTo(size * 0.8, size * 0.2);
  context.lineTo(size * 0.8, size * 0.8);
  context.lineTo(size * 0.44, size * 0.58);
  context.lineTo(size * 0.18, size * 0.58);
  context.closePath();
  context.fill();
  context.fillRect(size * 0.31, size * 0.57, size * 0.13, size * 0.25);
  context.restore();
}

function drawShoutOutIcon(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
) {
  if (shoutOutIcon?.complete && shoutOutIcon.naturalWidth > 0) {
    context.drawImage(shoutOutIcon, x, y, size, size);
    return;
  }
  drawFallbackShoutOutIcon(context, x, y, size);
}

export function preloadAmaShareAssets() {
  if (typeof Image === 'undefined' || shoutOutIcon) return;
  shoutOutIcon = new Image();
  shoutOutIcon.src = ICON_URL;
}

function createAmaBadgeCanvas({ question, accentColor }: AmaArtworkOptions): HTMLCanvasElement {
  preloadAmaShareAssets();

  const accent = safeHexColor(accentColor, '#7c3aed');
  const labelTextColor = isLightColor(accent) ? '#111827' : '#ffffff';
  const iconBackground = isLightColor(accent) ? '#111827' : accent;
  const normalizedQuestion = normalizeQuestion(question);
  const canvas = document.createElement('canvas');
  canvas.width = BADGE_WIDTH;
  canvas.height = 560;

  const measuringContext = canvas.getContext('2d');
  if (!measuringContext) throw new Error('Unable to create Ask Me Anything artwork');
  const { fontSize, lineHeight, lines } = fitQuestionText(measuringContext, normalizedQuestion);

  const questionHeight = lines.length * lineHeight;
  const boxX = BADGE_GUTTER;
  const boxY = BADGE_GUTTER;
  const boxWidth = BADGE_WIDTH - (BADGE_GUTTER * 2);
  const labelHeight = 48;
  const labelY = boxY + 24;
  const questionTop = labelY + labelHeight + 28;
  const footerHeight = 56;
  const boxHeight = (questionTop - boxY) + questionHeight + footerHeight;
  const ctaY = boxY + boxHeight + 14;
  const ctaHeight = 44;
  canvas.height = ctaY + ctaHeight + BADGE_GUTTER;

  // Assigning canvas height clears its drawing state.
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Unable to create Ask Me Anything artwork');
  context.clearRect(0, 0, canvas.width, canvas.height);

  context.save();
  context.shadowColor = 'rgba(0, 0, 0, 0.28)';
  context.shadowBlur = 18;
  context.shadowOffsetY = 7;
  roundedRect(context, boxX, boxY, boxWidth, boxHeight, 42);
  context.fillStyle = '#ffffff';
  context.fill();
  context.restore();

  roundedRect(context, boxX, boxY, boxWidth, boxHeight, 42);
  context.lineWidth = 6;
  context.strokeStyle = accent;
  context.stroke();

  const labelWidth = 276;
  const labelX = (BADGE_WIDTH - labelWidth) / 2;
  roundedRect(context, labelX, labelY, labelWidth, labelHeight, 24);
  context.fillStyle = accent;
  context.fill();

  context.fillStyle = labelTextColor;
  context.font = '800 22px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText('ASK ME ANYTHING', BADGE_WIDTH / 2, labelY + (labelHeight / 2) + 1);

  context.fillStyle = '#111827';
  context.font = `700 ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  const firstLineY = questionTop + (lineHeight / 2);
  lines.forEach((line, index) => {
    context.fillText(line, BADGE_WIDTH / 2, firstLineY + (index * lineHeight));
  });

  const logoCircleSize = 40;
  const logoCircleX = boxX + boxWidth - logoCircleSize - 18;
  const logoCircleY = boxY + boxHeight - logoCircleSize - 15;
  context.beginPath();
  context.arc(
    logoCircleX + (logoCircleSize / 2),
    logoCircleY + (logoCircleSize / 2),
    logoCircleSize / 2,
    0,
    Math.PI * 2,
  );
  context.fillStyle = iconBackground;
  context.fill();
  drawShoutOutIcon(context, logoCircleX + 7, logoCircleY + 7, logoCircleSize - 14);

  context.font = '700 16px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  const ctaWidth = Math.min(
    BADGE_WIDTH - (BADGE_GUTTER * 2),
    Math.ceil(context.measureText(CTA_TEXT).width) + 38,
  );
  const ctaX = (BADGE_WIDTH - ctaWidth) / 2;
  roundedRect(context, ctaX, ctaY, ctaWidth, ctaHeight, 22);
  context.fillStyle = 'rgba(17, 24, 39, 0.9)';
  context.fill();
  context.fillStyle = '#ffffff';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(CTA_TEXT, BADGE_WIDTH / 2, ctaY + (ctaHeight / 2) + 1);

  return canvas;
}

export function createAmaBadgeFile(options: AmaArtworkOptions): File {
  const canvas = createAmaBadgeCanvas(options);
  return dataUrlToFile(canvas.toDataURL('image/png'), `ask-me-anything-badge-${Date.now()}.png`);
}

export function createAmaStoryFile(options: AmaArtworkOptions): File {
  const canvas = document.createElement('canvas');
  canvas.width = STORY_WIDTH;
  canvas.height = STORY_HEIGHT;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Unable to create Instagram Story artwork');

  const gradientStart = safeHexColor(options.gradientStart, '#0a0a0a');
  const gradientEnd = safeHexColor(options.gradientEnd, '#1a1a2e');
  const isVertical = options.gradientDirection === 'to-b';
  const gradient = context.createLinearGradient(
    0,
    0,
    isVertical ? 0 : STORY_WIDTH,
    STORY_HEIGHT,
  );
  gradient.addColorStop(0, gradientStart);
  gradient.addColorStop(1, gradientEnd);
  context.fillStyle = gradient;
  context.fillRect(0, 0, STORY_WIDTH, STORY_HEIGHT);

  const badge = createAmaBadgeCanvas(options);
  const badgeWidth = 900;
  const badgeScale = badgeWidth / badge.width;
  const badgeHeight = Math.round(badge.height * badgeScale);
  const badgeX = Math.round((STORY_WIDTH - badgeWidth) / 2);
  const badgeY = Math.max(280, Math.round((STORY_HEIGHT - badgeHeight) / 3));
  context.drawImage(badge, badgeX, badgeY, badgeWidth, badgeHeight);

  return dataUrlToFile(canvas.toDataURL('image/png'), `ask-me-anything-story-${Date.now()}.png`);
}

function downloadFile(file: File) {
  const objectUrl = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = file.name;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

async function shareArtworkFile(file: File): Promise<AmaShareResult> {
  const shareNavigator = navigator as Navigator & {
    canShare?: (data?: ShareData) => boolean;
  };
  const shareData: ShareData = { files: [file] };

  if (typeof shareNavigator.share !== 'function') {
    downloadFile(file);
    return 'downloaded';
  }

  let fileSharingSupport: 'supported' | 'unsupported' | 'unknown' = 'unknown';
  if (shareNavigator.canShare) {
    try {
      fileSharingSupport = shareNavigator.canShare(shareData) ? 'supported' : 'unsupported';
    } catch {
      fileSharingSupport = 'unsupported';
    }
  }

  if (fileSharingSupport === 'unsupported') {
    downloadFile(file);
    return 'downloaded';
  }

  try {
    await shareNavigator.share(shareData);
    return 'shared';
  } catch (error) {
    const errorName = typeof error === 'object' && error !== null && 'name' in error
      ? String(error.name)
      : '';
    if (errorName === 'AbortError') {
      return 'cancelled';
    }

    // Browsers without canShare() may expose navigator.share() while lacking
    // file support. Preserve the download fallback only for that explicit
    // unsupported case; permission/state failures should surface to the UI
    // instead of unexpectedly reopening Safari's download bar.
    if (
      fileSharingSupport === 'unknown'
      && (error instanceof TypeError || errorName === 'NotSupportedError')
    ) {
      downloadFile(file);
      return 'downloaded';
    }

    throw error;
  }
}

export async function shareAmaBadge(options: AmaArtworkOptions): Promise<AmaShareResult> {
  return shareArtworkFile(createAmaBadgeFile(options));
}

export async function shareAmaStory(options: AmaArtworkOptions): Promise<AmaShareResult> {
  return shareArtworkFile(createAmaStoryFile(options));
}

preloadAmaShareAssets();
